import { computed, ref, watch, Ref } from '@vue/composition-api';
import { debounce } from 'lodash-es';
import { InterestBehaviorScene, InterestBehaviorData, AdgroupFormData, CampaignFormData } from '@tt4b/ttam-enums';
import { useInterestBehaviorStore } from './use-interest-behavior-store';
import { useStdAdgroupIs, similarSearch } from '@tt4b/ttam-hooks';
import { getCurrentLang } from '@tt4b/ttam-utils';
import { i18n } from './../../../../i18n';
import { useBehaviorData } from './../../../../hooks/creation/adgroup/useBehavior/index';
import { generateId, formatHashtagLabel } from './../../../../utils/index';
import { useInterestBehaviorData } from './use-interest-behavior-data';

const similarRecommendSceneMap = ref({});
const similarRecommendList = ref<InterestBehaviorData[]>([]);

export function useSimilarRecommend(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  emit: any;
}) {
  const { adgroupData: formData, campaignData, emit } = props;

  const { INTEREST, HASHTAG, KEYWORD } = InterestBehaviorScene;
  const { interestBehaviorListMap, updatePanelData } = useInterestBehaviorStore();
  const { isSelectedIndustryType } = useStdAdgroupIs({ adgroupFormData: formData, campaignFormData: campaignData });

  const { hashtagCategories, hashtagData, getChangedBehaviorData } = useBehaviorData({
    adgroupData: formData,
  });
  const adTagV2 = computed(() => formData.value.adTagV2);
  const interestKeywordsLangI18n = computed(() => formData.value.interestKeywordsLangI18n);
  const interestKeywordsI18n = computed(() => formData.value.interestKeywordsI18n);

  const currentLang = getCurrentLang();
  const { selectedTags } = useInterestBehaviorData({ adgroupData: formData, emit });

  const isShowSimilarRecommend = computed(() => similarRecommendList.value.length && selectedTags.value.length);

  const similarRecommendTitle = computed(() => {
    const hasHashtag = Boolean(similarRecommendSceneMap.value[HASHTAG]?.length);
    const hasInterestKeyword = Boolean(similarRecommendSceneMap.value[KEYWORD]?.length);
    if (hasHashtag && hasInterestKeyword) {
      return i18n.t('module_adgroup_similar_interests_hashtags');
    }
    if (hasInterestKeyword) {
      return i18n.t('module_adgroup_similar_interests');
    }

    if (hasHashtag) {
      return i18n.t('module_adgroup_similar_hashtags');
    }

    return i18n.t('module_adgroup_similar_interests_hashtags');
  });
  // 请求相似推荐
  const getSimilarRecommend = debounce(
    async () => {
      if (isSelectedIndustryType.value) {
        return [];
      }
      if (
        adTagV2.value?.length === 0 &&
        interestKeywordsI18n.value?.length === 0 &&
        hashtagCategories.value?.length === 0
      ) {
        return [];
      }
      const adTagV2List = computed(() => adTagV2.value || []);
      const interestKeywordsI18nList = computed(() => interestKeywordsI18n.value || []);
      const hashtagCategoriesList = computed(() => hashtagCategories.value || []);
      const scene_tags: { scene: number; tags: string[] }[] = [];
      const hashtagMap = interestBehaviorListMap.value[HASHTAG];
      const interestMap = interestBehaviorListMap.value[INTEREST];
      const hashtags = hashtagCategoriesList.value
        .map(id => hashtagMap && hashtagMap[id]?.label)
        .filter(label => label);
      const keywords = [...interestKeywordsI18nList.value, ...adTagV2List.value]
        .map(id => interestMap && interestMap[id]?.tag_name)
        .filter(label => label);

      if (
        hashtags.length !== hashtagCategoriesList.value.length ||
        keywords.length !== [...interestKeywordsI18nList.value, ...adTagV2List.value].length
      ) {
        return;
      }

      if (hashtagCategoriesList.value.length) {
        scene_tags.push({
          scene: HASHTAG,
          tags: hashtags,
        });
      }

      if ((interestKeywordsI18nList.value.length || adTagV2List.value.length) && interestMap) {
        scene_tags.push({
          scene: KEYWORD,
          tags: keywords,
        });
      }
      const tags = await similarSearch({
        scene_tags,
        inventoryFlow: formData.value.inventoryFlow,
        industryTypes: campaignData.value.industryTypes,
      });
      similarRecommendList.value = tags
        .filter(
          tag =>
            !interestKeywordsI18nList.value?.includes(tag.tag_id) && !hashtagCategoriesList.value?.includes(tag.tag_id),
        )
        .map(tag => ({
          ...tag,
          id: generateId(tag.scene, tag.tag_id),
          label: tag.tag_name,
          tag_name: tag.scene === HASHTAG ? formatHashtagLabel(tag.tag_name) : tag.tag_name,
        }));
    },
    300,
    { leading: false, trailing: true, maxWait: 500 },
  );
  // 当选中tag变化时都重新请求
  function subscribeSimilarRecommend() {
    watch(
      () => [adTagV2.value, interestKeywordsI18n.value, hashtagCategories.value],
      () => getSimilarRecommend(),
      {
        immediate: true,
      },
    );
  }

  function selectOneSimilarRecommend(tag, index) {
    updatePanelData(similarRecommendList.value.splice(index, 1));
    if (
      (tag.scene === HASHTAG && hashtagCategories?.value?.includes(tag.tag_id)) ||
      (tag.scene === KEYWORD && interestKeywordsI18n?.value?.includes(tag.tag_id))
    ) {
      return;
    }
    let tempData = {};
    if (tag.scene === HASHTAG) {
      const categories = [...hashtagCategories.value, tag.tag_id];
      const data = getChangedBehaviorData({ ...hashtagData.value, categories }, 'hashtag');
      tempData = { ...tempData, ...data };
    }

    if (tag.scene === KEYWORD) {
      tempData = {
        ...tempData,
        interestKeywordsI18n: [...interestKeywordsI18n.value, tag.tag_id],
        interestKeywordsLangI18n: [...interestKeywordsLangI18n.value, tag.lang || currentLang],
      };
    }
    emit('change', tempData);
  }

  function getTagIdBySceneFromSimilarRecommendList(scene) {
    const selectData = {
      [HASHTAG]: hashtagCategories?.value,
      [KEYWORD]: interestKeywordsI18n?.value,
    };

    return (
      similarRecommendList.value
        ?.filter(tag => tag.scene === scene && !selectData[scene]?.includes(tag.tag_id))
        .map(tag => tag.tag_id) || []
    );
  }

  function selectAllSimilarRecommend() {
    updatePanelData(similarRecommendList.value);
    const hashtag = getTagIdBySceneFromSimilarRecommendList(HASHTAG);
    const interestKeywords = getTagIdBySceneFromSimilarRecommendList(KEYWORD);
    let interestKeywordsLang: string[] = [];
    if (interestKeywords?.length) {
      interestKeywordsLang = interestKeywords?.map(id => interestBehaviorListMap.value[id]?.lang || currentLang) || [];
    }
    let tempData = {};
    const categories = [...hashtagCategories.value, ...hashtag];
    const data = getChangedBehaviorData({ ...hashtagData.value, categories }, 'hashtag');
    tempData = {
      ...tempData,
      ...data,
      interestKeywordsI18n: [...interestKeywordsI18n.value, ...interestKeywords],
      interestKeywordsLangI18n: [...interestKeywordsLangI18n.value, ...interestKeywordsLang],
    };
    similarRecommendList.value = [];
    emit('change', tempData);
  }

  return {
    isShowSimilarRecommend,
    similarRecommendList,
    similarRecommendSceneMap,
    similarRecommendTitle,
    getSimilarRecommend,
    selectOneSimilarRecommend,
    selectAllSimilarRecommend,
    subscribeSimilarRecommend,
  };
}
